import { Injectable, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { QrMessage } from '../models/models-signalr/qr-message.model';
import { SIGNALR_METHODS, SIGNALR_CONNECTION_STATE } from '../constants/signalr-methods';
// Emitimos sólo el `cardNo` como string para simplificar el consumo

@Injectable({ providedIn: 'root' })
export class SignalrService {
  private hubConnection: signalR.HubConnection | null = null;

  readonly qrMessage = signal<string | null>(null);
  readonly refreshLockers = signal<number | null>(null);
  readonly connectionState = signal<string>(SIGNALR_CONNECTION_STATE.DISCONNECTED);

  startConnection(middlewareIP: string): void {
    if (this.hubConnection) return;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${middlewareIP}/hubqr`)
      .withAutomaticReconnect()
      .build();

    this.hubConnection.on(SIGNALR_METHODS.RECEIVE_QR, (payload: QrMessage) => {
      const cardNo = payload.params.events
        .find(e => e.data.cardNo.trim() !== '')
        ?.data.cardNo.trim();

      if (cardNo) {
        this.qrMessage.set(null); // resetear para que siempre dispare aunque el valor sea igual
        this.qrMessage.set(cardNo);
      }
    });

    this.hubConnection.on(SIGNALR_METHODS.REFRESH_LOCKERS, () => {
      this.refreshLockers.set(null); // resetear para que siempre dispare aunque el valor sea igual
      this.refreshLockers.set(Date.now());
      console.log('SignalR: refreshLockers event received');
    });

    this.hubConnection.onreconnecting(() => {
      this.connectionState.set(SIGNALR_CONNECTION_STATE.RECONNECTING);
    });

    this.hubConnection.onreconnected(() => {
      this.connectionState.set(SIGNALR_CONNECTION_STATE.CONNECTED);
    });

    this.hubConnection.onclose(() => {
      this.connectionState.set(SIGNALR_CONNECTION_STATE.DISCONNECTED);
      this.scheduleReconnect();
    });

    this.connect();
  }

  private connect(): void {
    if (!this.hubConnection) return;
    this.hubConnection
      .start()
      .then(() => this.connectionState.set(SIGNALR_CONNECTION_STATE.CONNECTED))
      .catch((err) => {
        this.connectionState.set(SIGNALR_CONNECTION_STATE.DISCONNECTED);
        console.error('SignalR connection error:', err);
        this.scheduleReconnect();
      });
  }

  // sirve para reintentar despues de los reintentos iniciales, despues de 5 segundos y ciclarse hasta que se conecte
  private scheduleReconnect(): void {
    if (!this.hubConnection) return; // stopConnection fue llamado, no reintentar
    setTimeout(() => this.connect(), 5000);
  }

  stopConnection(): void {
    const conn = this.hubConnection;
    this.hubConnection = null; // nullear antes del stop para que scheduleReconnect no reintente
    conn?.stop().catch((err) => console.error('SignalR stop error:', err));
    this.connectionState.set(SIGNALR_CONNECTION_STATE.DISCONNECTED);
  }
}
